"use client";

import { ChevronDown, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

export interface FloatingAudioControlProps {
  /** Whether the control is visible */
  isVisible: boolean;
  /** Primary action button (play/pause or record/stop) */
  primaryButton: ReactNode;
  /** Title content (track name or "Recording...") */
  title: ReactNode;
  /** Time display ("0:45 / 3:21" or "0:45") */
  timeDisplay: ReactNode;
  /** Progress bar or level meter */
  meter: ReactNode;
  /** Actions always visible below the meter (e.g., Save/Discard) */
  actions?: ReactNode;
  /** Content shown when expanded (additional controls) */
  expandedContent?: ReactNode;
  /** Called when close button is clicked (used by default close button) */
  onClose?: () => void;
  /** Custom close button (replaces default X button when collapsed) */
  closeButton?: ReactNode;
  /** Width when collapsed (default: "16rem") */
  compactWidth?: string;
  /** Width when expanded (default: "20rem") */
  expandedWidth?: string;
  /** Aria label for expand button (default: "Expand") */
  expandLabel?: string;
  /** Aria label for collapse button (default: "Collapse") */
  collapseLabel?: string;
  /** Aria label for close button (default: "Close") */
  closeLabel?: string;
  /** Test ID for e2e testing */
  testId?: string;
}

/**
 * Shared floating container for audio controls (player and recorder).
 * Provides consistent positioning, styling, and expand/collapse behavior.
 */
export function FloatingAudioControl({
  isVisible,
  primaryButton,
  title,
  timeDisplay,
  meter,
  actions,
  expandedContent,
  onClose,
  closeButton,
  compactWidth = "16rem",
  expandedWidth = "20rem",
  expandLabel = "Expand",
  collapseLabel = "Collapse",
  closeLabel = "Close",
  testId,
}: FloatingAudioControlProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Trigger entrance animation when control becomes visible
  useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
    if (!isVisible) {
      setHasAnimated(false);
      setIsExpanded(false);
    }
  }, [isVisible, hasAnimated]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={hasAnimated ? "animate-slide-up" : undefined}
      data-testid={testId}
      style={{
        position: "fixed",
        bottom: "1rem",
        right: "1rem",
        zIndex: 9999,
      }}
    >
      <div
        className="rounded-xl border border-border-subtle bg-bg-secondary shadow-2xl"
        style={{
          width: isExpanded ? expandedWidth : compactWidth,
          transition: "width 0.2s ease-out",
        }}
      >
        {/* Compact view - always visible */}
        <div className="p-3">
          {/* Primary action and info */}
          <div className="flex items-center gap-3">
            {primaryButton}

            {/* Title and time info */}
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="min-w-0 flex-1 text-left"
              aria-label={isExpanded ? collapseLabel : expandLabel}
            >
              <span className="block truncate text-sm font-medium text-text-primary">
                {title}
              </span>
              <p className="truncate font-mono text-xs text-text-muted">
                {timeDisplay}
              </p>
            </button>

            {/* Close/Collapse button */}
            {isExpanded ? (
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="shrink-0 rounded p-1.5 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                aria-label={collapseLabel}
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              (closeButton ?? (
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded p-1.5 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                  aria-label={closeLabel}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              ))
            )}
          </div>

          {/* Meter area (progress bar or level meter) */}
          {meter && <div className="mt-2">{meter}</div>}

          {/* Always-visible actions */}
          {actions && <div className="mt-3">{actions}</div>}
        </div>

        {/* Expanded content */}
        {isExpanded && expandedContent && (
          <div className="border-t border-border-subtle px-3 py-2">
            {expandedContent}
          </div>
        )}
      </div>
    </div>
  );
}

export interface ProgressBarProps {
  /** Current time in seconds */
  currentTime: number;
  /** Total duration in seconds */
  duration: number;
  /** Called when user seeks to a new position */
  onSeek: (time: number) => void;
}

/**
 * Seekable progress bar for audio playback.
 */
export function ProgressBar({
  currentTime,
  duration,
  onSeek,
}: ProgressBarProps) {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <input
      type="range"
      min={0}
      max={duration || 100}
      value={currentTime}
      onChange={(e) => onSeek(Number.parseFloat(e.target.value))}
      className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-bg-tertiary accent-amber"
      style={{
        background: `linear-gradient(to right, var(--color-amber) ${progress}%, var(--color-bg-tertiary) ${progress}%)`,
      }}
    />
  );
}

export interface PrimaryButtonProps {
  /** Icon to display */
  icon: ReactNode;
  /** Called when button is clicked */
  onClick: () => void;
  /** Accessible label */
  ariaLabel: string;
  /** Button size variant */
  size?: "normal" | "compact";
  /** Color variant */
  variant?: "amber" | "red";
}

/**
 * Primary action button for floating audio controls.
 * Used for play/pause (amber) or record/stop (red).
 */
export function PrimaryButton({
  icon,
  onClick,
  ariaLabel,
  size = "normal",
  variant = "amber",
}: PrimaryButtonProps) {
  const sizeClasses = size === "compact" ? "p-2.5" : "p-3 shadow-md";
  const colorClasses =
    variant === "amber"
      ? "bg-amber text-on-accent hover:bg-amber-warm"
      : "bg-red-500 text-white hover:bg-red-600";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full transition-colors ${sizeClasses} ${colorClasses}`}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  );
}
